import React, { useState } from 'react';
import styled from '@emotion/styled';

import Icon from '~Scenes/SelfServiceKiosks/Components/Icon';
import ArrowBack from '~Assets/icons/arrow_back.svg';
import ArrowForward from '~Assets/icons/arrow_forward.svg';
import Basket from './Basket';
import type { Choice, LocalUpsell, Order } from '../../..';
import TouchedWrapper from '~Scenes/SelfServiceKiosks/Components/TouchedWrapper';

const Wrapper = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-content: center;
  width: 496px;
  margin: 0 auto;
`;
const Shadow = styled.div`
  position: fixed;
  bottom: 198px;
  left: 0;
  right: 0;
  height: 272px;
  background: linear-gradient(0deg, rgb(255, 255, 255) 0%, rgba(245, 245, 248, 0) 100%);
  z-index: -1;
  pointer-events: none;

  @media (orientation: landscape) {
    bottom: 0;
    height: 100px;
    background: linear-gradient(180deg, rgba(245, 245, 248, 0) 0%, rgb(255, 255, 255, 0.5) 100%);
  }
`;
const Background = styled.div`
  height: 200px;
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  background: #fff;
  z-index: -1;

  @media (orientation: landscape) {
    display: none;
  }
`;

const Fade = styled.div<{ visible: boolean }>`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  transition: 0.5s visibility, 0.5s opacity;
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 auto 0;
  position: relative;
`;
const Prev = styled(TouchedWrapper)`
  background: rgb(255, 255, 255);
  border-radius: 32px;
  height: 88px;
  width: 120px;
  margin-right: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (orientation: landscape) {
    height: 66px;
  }
`;
const Next = styled(TouchedWrapper)`
  background: ${({ theme }) => theme.brandColor2};
  border-radius: 32px;
  height: 88px;
  width: 344px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px 0 32px;
  box-sizing: border-box;

  @media (orientation: landscape) {
    height: 66px;
  }
`;
const TitleNext = styled.span`
  color: ${({ theme }) => theme.complimentaryColor2};
  font-family: Eina01-SemiBold;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 20px;
  flex-grow: 1;
`;
const Dots = styled.div`
  display: flex;
  align-items: center;
  margin: 72px auto 80px;

  @media (orientation: landscape) {
    margin: 40px auto 30px;
  }
`;
const Dot = styled.div<{ active: boolean }>`
  background: ${({ active }) => (active ? 'rgb(255, 255, 255)' : 'rgb(189, 189, 192)')};
  border: 2px solid ${({ active }) => (active ? 'rgb(66, 66, 69)' : 'rgb(189, 189, 192)')};
  border-radius: 50%;
  box-sizing: border-box;
  height: 16px;
  width: 16px;
  margin: 0 8px;
  transition: 0.3s background, 0.3s border;
`;

interface Props {
  upsells: LocalUpsell[];
  changeStep: (step: number) => void;
  actionNextButton: () => void;
  onRemoveUpsellItem: (id: string) => void;
  step: number;
  order: Order;
  title: string;
  price: number;
  choicesArr: Choice[];
}

const BottomNavigation = ({
  step,
  changeStep,
  upsells,
  title,
  actionNextButton,
  onRemoveUpsellItem,
  ...rest
}: Props) => {
  const [active, setActive] = useState(false);

  return (
    <Wrapper>
      <Shadow />
      <Background />
      <Fade visible={active} onClick={() => setActive(false)} />
      <Basket {...rest} active={active} setActive={setActive} onRemoveUpsellItem={onRemoveUpsellItem} />
      <Row>
        <Prev
          onClick={() => changeStep(step - 1)}
          shadows={{
            pressed: '0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 1px 18px 0px rgba(0, 0, 0, 0.12)',
            init: '0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 1px 18px 0px rgba(0, 0, 0, 0.12)',
          }}>
          <Icon icon={ArrowBack} color="#212124" size={40} />
        </Prev>
        <Next
          onClick={actionNextButton}
          shadows={{
            pressed: '0px 11px 15px -7px rgba(0, 0, 0, 0.32)',
            init: '0px 11px 15px -7px rgba(0, 0, 0, 0.32), 0px 9px 46px 8px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.24)',
          }}>
          <TitleNext>{title}</TitleNext>
          <Icon icon={ArrowForward} color="#fff" size={40} />
        </Next>
      </Row>

      <Dots>
        {upsells.map((upsell, idx) => (
          <Dot key={idx} active={idx === step} onClick={() => changeStep(idx)} />
        ))}
      </Dots>
    </Wrapper>
  );
};

export default BottomNavigation;
